import { useContext } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@utilities-one/gpm-ui';

import type { AccessUser } from '@/entities/user/types';
import { OptionalPortalContainerContext } from '@/library/portal/OptionalPortalContainerContext';

/** One `{ id, name }` entry of a name-sorted available-markets list (login and `me` payloads). */
export type MarketOption = AccessUser['availableMarkets'][number];

interface Props {
  markets: MarketOption[];
  value: string;
  onValueChange: (marketId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label': string;
}

/**
 * Market select shared by the post-login MarketSelectDialog and the topbar
 * MarketSwitcher. Portal-aware the way `FormSelect` is: inside a dialog's
 * `DialogPortalLayer` the popup portals into the dialog subtree (outside it
 * the Base UI dialog blocks the popup's pointer events); elsewhere the
 * context is null and the default portal applies.
 */
export const MarketSelect = ({
  markets,
  value,
  onValueChange,
  placeholder = 'Select market',
  disabled,
  className,
  'aria-label': ariaLabel,
}: Props) => {
  const portalHostEl = useContext(OptionalPortalContainerContext);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={className} aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent {...(portalHostEl ? { portalContainer: portalHostEl, position: 'popper' as const } : {})}>
        {markets.map((market) => (
          <SelectItem key={market.id} value={market.id}>
            {market.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
